import { showSuccess, showError } from './toast';

const API_BASE_URL = 'https://v2backend.kenhtooshin.com/api/v2';

export interface ApiResponse<T = unknown> {
  success: boolean;
  status: number;
  message: string;
  data: T;
  timestamp: string;
  request_id: string;
  errors?: ValidationError[];
}

export interface ValidationError {
  field?: string;
  message: string;
  code?: string;
}

export interface User {
  id: number;
  name: string;
  phone: string;
  user_code: string;
  balance: string;
  p_balance: string;
  block: number;
  point: number;
  last_seen: string;
  created_at: string;
  updated_at?: string;
}

export interface LoginResponse {
  user: User;
  token: string;
  token_type: string;
  expires_at: string;
  expires_in: number;
}

export interface HomePageData {
  u_popup: Array<{
    id: number;
    title: string;
    image: string;
    status: string;
    start_date: string;
    end_date: string;
  }>;
  u_header: {
    text: string;
  };
  u_banner: Array<{
    id: number;
    image: string;
    mb_image: string;
  }>;
  u_lottery: Array<{
    id: number;
    name: string;
    image: string;
    active: number;
  }>;
  u_gamecategory: Array<{
    id: number;
    name: string;
    image: string;
    active: number;
  }>;
  u_hot_game: Array<{
    id: number;
    name: string;
    image: string;
    is_hot: number;
    active: number;
    g_code?: string;
    p_code?: string;
    cate_code?: string;
    category_id?: number;
    provider_id?: number;
  }>;
  u_buffalo_games: Array<{
    id: number;
    name: string;
    image: string;
    is_hot: number;
    active: number;
    g_code?: string;
    p_code?: string;
    cate_code?: string;
    category_id?: number;
    provider_id?: number;
  }>;
  u_hot_gameprovider: Array<{
    id: number;
    name: string;
    image: string;
    hot: number;
    active: number;
  }>;
  u_new_game: Array<{
    id: number;
    name: string;
    image: string;
    is_new: number;
    active: number;
    g_code?: string;
    p_code?: string;
    cate_code?: string;
    category_id?: number;
    provider_id?: number;
  }>;
}

export interface GameCategory {
  id: number;
  name: string;
  game_code: string;
  code: string;
  image: string;
  active: boolean;
}

export interface GameProvider {
  id: number;
  name: string;
  image: string;
  sec_image: string;
  p_code: string;
  active: boolean;
  hot: boolean;
  currency: string;
  connectplus_category_id?: number;
}

export interface GameCategory {
  id: number;
  name: string;
  game_code: string;
  code: string;
  image: string;
  active: boolean;
}

export interface Game {
  id: number;
  name: string;
  g_code: string;
  html_type: string;
  active: boolean;
  is_hot: boolean;
  is_new: boolean;
  image: string;
  cate_code: string;
  p_code: string;
  category_id: number;
  provider_id: number;
  provider: GameProvider;
  category: GameCategory;
}

export interface GamesByCategoryResponse {
  category: GameCategory;
  games: Game[];
  providers: GameProvider[];
}

export interface GamesByProviderResponse {
  provider: GameProvider;
  games: Game[];
}

export interface AllGamesResponse {
  games: Game[];
  pagination: {
    current_page: number;
    per_page: number;
    total_games: number;
    total_pages: number;
    has_next_page: boolean;
  };
}

export interface GameCategoriesResponse {
  categories: GameCategory[];
}

export interface ProvidersByCategoryResponse {
  category: GameCategory;
  providers: GameProvider[];
  total_providers: number;
}

export interface Wallet {
  id: number;
  payment_method_id: number;
  account_name: string;
  account_number: string;
  payment_method: {
    id: number;
    name: string;
    logo: string;
    type: string;
  };
  created_at: string;
  updated_at: string;
}

export interface SaveWalletRequest {
  payment_id: string;
  payment_method_id: number;
  account_name: string;
  account_number: string;
}

export interface SaveWalletResponse {
  wallet: Wallet;
}

export interface GetWalletsResponse {
  wallets: Wallet[];
  total_wallets: number;
  max_wallets: number;
}

export interface PaymentMethod {
  id: number;
  name: string;
  logo: string;
  image: string;
  type: string;
  percentage: number;
  cashout_status: boolean;
  account_numbers?: Array<{
    account_number: string;
    account_name: string;
  }>;
}

export interface LaunchGameRequest {
  product_id: string;
  game_type: string;
  game_id: string;
  platform: string;
}

export interface LaunchGameResponse {
  game_url: string;
  game_id: string;
  provider: string;
}

export interface TransactionHistory {
  id: number;
  amount: string;
  old_amount: string;
  after_amount: number;
  status: string;
  payment_method?: {
    id: number;
    name: string;
    logo: string;
    type: string;
  };
  transaction_id?: string;
  voucher_code?: string;
  holder_phone?: string;
  created_at: string;
  updated_at: string;
}

export interface PaginationInfo {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  from: number;
  to: number;
}

export interface CashInHistoryResponse {
  cash_ins: TransactionHistory[];
  pagination: PaginationInfo;
}

export interface CashOutHistoryResponse {
  cash_outs: TransactionHistory[];
  pagination: PaginationInfo;
}

class ApiService {
  private baseURL: string;
  private token: string | null = null;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
    this.loadToken();
  }

  private loadToken() {
    if (typeof window !== 'undefined') {
      this.token = localStorage.getItem('auth_token');
    }
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;
    
    // Validate URL
    try {
      new URL(url);
    } catch (error) {
      console.error('Invalid URL constructed:', url, error);
      throw new Error(`Invalid URL: ${url}`);
    }
    
    const config: RequestInit = {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      
      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('Response is not JSON:', contentType);
        throw new Error('Invalid response format');
      }

      // Get response text first
      const responseText = await response.text();
      
      if (!responseText.trim()) {
        console.error('Empty response received');
        throw new Error('Empty response');
      }

      // Try to parse JSON
      let data: Record<string, unknown>;
      try {
        data = JSON.parse(responseText) as Record<string, unknown>;
      } catch (jsonError) {
        console.error('JSON parse error:', jsonError);
        console.error('Response text:', responseText.substring(0, 200));
        throw new Error('Invalid JSON response');
      }

      if (!response.ok) {
        // Handle token expiration
        if (response.status === 401) {
          this.clearToken();
          if (typeof window !== 'undefined') {
            localStorage.removeItem('user_data');
          }
          // Don't show error for 401 as it will be handled by the auth context
          throw new Error('Unauthorized');
        }
        
        // Handle validation errors (400 status)
        if (response.status === 400) {
          let errorMessage = (data.message as string) || 'Validation failed';
          
          // Check for errors array
          if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
            const firstError = data.errors[0];
            if (typeof firstError === 'string') {
              errorMessage = firstError;
            } else if (firstError && typeof firstError === 'object' && 'message' in firstError) {
              errorMessage = (firstError.message as string) || errorMessage;
            }
          }
          
          // Check for error object with field-specific errors
          else if (data.error && typeof data.error === 'object') {
            const errorEntries = Object.entries(data.error);
            if (errorEntries.length > 0) {
              const [field, messages] = errorEntries[0];
              if (Array.isArray(messages) && messages.length > 0) {
                errorMessage = `${field}: ${messages[0]}`;
              } else if (typeof messages === 'string') {
                errorMessage = `${field}: ${messages}`;
              }
            }
          }
          
          showError(errorMessage);
          throw new Error(errorMessage);
        }
        
        // Handle other errors
        const errorMessage = (data.message as string) || 'Request failed';
        showError(errorMessage);
        throw new Error(errorMessage);
      }

      return data as unknown as ApiResponse<T>;
    } catch (error) {
      console.error('API Request failed:', error);
      if (error instanceof Error && 
          !error.message.includes('Request failed') && 
          !error.message.includes('Unauthorized') &&
          !error.message.includes('Validation failed') &&
          !error.message.includes('Validation error') &&
          !error.message.includes(':')) { // Don't show network error for validation errors
        showError('Network error. Please check your connection.');
      }
      throw error;
    }
  }

  // Authentication methods
  async register(data: {
    name: string;
    phone: string;
    password: string;
  }): Promise<ApiResponse> {
    const response = await this.request('/user/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    
    if (response.success) {
      showSuccess('Registration successful! Please verify your OTP.');
    }
    
    return response;
  }

  async verifyOtp(data: {
    phone: string;
    otp: string;
  }): Promise<ApiResponse<LoginResponse>> {
    const response = await this.request<LoginResponse>('/user/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (response.success && response.data.token) {
      this.setToken(response.data.token);
      showSuccess('OTP verified successfully! Welcome to B Bold!');
    }

    return response;
  }

  async login(data: {
    phone: string;
    password: string;
    device_name?: string;
    device_token?: string;
  }): Promise<ApiResponse<LoginResponse>> {
    const response = await this.request<LoginResponse>('/user/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (response.success && response.data.token) {
      this.setToken(response.data.token);
      showSuccess('Login successful! Welcome back!');
    }

    return response;
  }

  async logout(): Promise<ApiResponse> {
    const response = await this.request('/user/auth/logout', {
      method: 'POST',
    });

    this.clearToken();
    if (response.success) {
      showSuccess('Logged out successfully!');
    }
    return response;
  }

  async forgotPassword(data: { phone: string }): Promise<ApiResponse> {
    const response = await this.request('/user/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    
    if (response.success) {
      showSuccess('Password reset OTP sent to your phone!');
    }
    
    return response;
  }

  async resetPassword(data: {
    phone: string;
    otp: string;
    new_password: string;
    confirm_password: string;
  }): Promise<ApiResponse> {
    const response = await this.request('/user/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    
    if (response.success) {
      showSuccess('Password reset successfully! Please login with your new password.');
    }
    
    return response;
  }

  async changePassword(data: {
    current_password: string;
    new_password: string;
    confirm_password: string;
  }): Promise<ApiResponse> {
    const response = await this.request('/user/change-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (response.success) {
      showSuccess('Password changed successfully! Please login again.');
      this.clearToken();
    }
    
    return response;
  }

  // Payment methods
  async getPaymentMethods(type: 'cashin' | 'cashout'): Promise<ApiResponse & { img_pre_fix?: string }> {
    return this.request(`/user/payment-methods/${type}`) as Promise<ApiResponse & { img_pre_fix?: string }>;
  }

  // Cash In
  async cashIn(data: {
    payment_id: number;
    amount: number;
    transaction_id: string;
    account_name?: string;
    user_phone?: string;
    holder_phone: string;
    promo_id?: number;
    side?: string;
  }): Promise<ApiResponse> {
    const response = await this.request('/user/cash-in', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (response.success) {
      showSuccess('Cash-in request submitted successfully!');
    }
    
    return response;
  }

  // Cash Out
  async cashOut(data: {
    amount: number;
    password: string;
  }): Promise<ApiResponse> {
    const response = await this.request('/user/cash-out', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (response.success) {
      showSuccess('Cash-out request submitted successfully!');
    }
    
    return response;
  }

  // Game Management API methods
  async getGamesByCategory(categoryId: number): Promise<ApiResponse<GamesByCategoryResponse>> {
    return this.request<GamesByCategoryResponse>(`/games/by-category?category_id=${categoryId}`);
  }

  async getGamesByProvider(providerId: number): Promise<ApiResponse<GamesByProviderResponse>> {
    return this.request<GamesByProviderResponse>(`/games/by-provider?provider_id=${providerId}`);
  }

  async getAllGames(filters?: {
    category_id?: number;
    provider_id?: number;
    page?: number;
  }): Promise<ApiResponse<AllGamesResponse>> {
    const params = new URLSearchParams();
    if (filters?.category_id) params.append('category_id', filters.category_id.toString());
    if (filters?.provider_id) params.append('provider_id', filters.provider_id.toString());
    if (filters?.page) params.append('page', filters.page.toString());
    
    const queryString = params.toString();
    const endpoint = queryString ? `/games/all?${queryString}` : '/games/all';

    return this.request<AllGamesResponse>(endpoint);
  }

  async getProvidersByCategory(categoryId: number): Promise<ApiResponse<ProvidersByCategoryResponse>> {
    return this.request<ProvidersByCategoryResponse>(`/games/providers/by-category?category_id=${categoryId}`);
  }

  async getGameCategories(): Promise<ApiResponse<GameCategoriesResponse>> {
    return this.request<GameCategoriesResponse>('/games/categories');
  }

  // Wallet Management API methods
  async getWallets(): Promise<ApiResponse<GetWalletsResponse>> {
    return this.request<GetWalletsResponse>('/user/wallet');
  }

  async saveWallet(data: SaveWalletRequest): Promise<ApiResponse<SaveWalletResponse>> {
    return this.request<SaveWalletResponse>('/user/wallet', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async launchGame(data: LaunchGameRequest): Promise<ApiResponse<LaunchGameResponse>> {
    console.log('API: Launching game with data:', data);
    const response = await this.request<LaunchGameResponse>('/games/launch', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    console.log('API: Launch game response:', response);
    if (response.success) {
      showSuccess('Game launched successfully!');
    }
    return response;
  }

  // User methods
  async getUserProfile(): Promise<ApiResponse<{
    profile: User;
    recent_activities: Array<unknown>;
    statistics: unknown;
  }>> {
    return this.request('/user/profile');
  }

  // Homepage methods
  async getHomePageData(): Promise<ApiResponse<HomePageData> & { img_pre_fix: string }> {
    return this.request('/user/home/page') as Promise<ApiResponse<HomePageData> & { img_pre_fix: string }>;
  }

  // Transaction History methods
  async getCashInHistory(page: number = 1, perPage: number = 10, startDate?: string, endDate?: string): Promise<ApiResponse<CashInHistoryResponse>> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('per_page', perPage.toString());
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    return this.request<CashInHistoryResponse>(`/user/cash-in?${params.toString()}`);
  }

  async getCashOutHistory(page: number = 1, perPage: number = 10, startDate?: string, endDate?: string): Promise<ApiResponse<CashOutHistoryResponse>> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('per_page', perPage.toString());
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    return this.request<CashOutHistoryResponse>(`/user/cash-out?${params.toString()}`);
  }

  // Token management
  setToken(token: string) {
    this.token = token;
    if (typeof window !== 'undefined') {
      localStorage.setItem('auth_token', token);
    }
  }

  clearToken() {
    this.token = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('auth_token');
    }
  }

  getToken(): string | null {
    return this.token;
  }

  isAuthenticated(): boolean {
    if (!this.token) return false;
    
    // Check if token exists in localStorage as well
    if (typeof window !== 'undefined') {
      const storedToken = localStorage.getItem('auth_token');
      if (!storedToken || storedToken !== this.token) {
        this.token = storedToken;
      }
    }
    
    return !!this.token;
  }

  // Check if token is expired (if token contains expiration info)
  isTokenExpired(): boolean {
    if (!this.token) return true;
    
    try {
      // If token is JWT, decode and check expiration
      const payload = JSON.parse(atob(this.token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch {
      // If token is not JWT or can't be decoded, assume it's valid
      return false;
    }
  }

  // Lottery API methods
  async getLotterySections2D(): Promise<ApiResponse<{
    sections: Array<{
      id: number;
      time_section: string;
      open_time: string;
      close_time: string;
      is_open: boolean;
      odd: number;
      is_maintain: boolean;
      formatted_time: string;
      formatted_open_time: string;
      formatted_close_time: string;
    }>;
    total_sections: number;
    is_opened_today: boolean;
    current_time: string;
    category: string;
    category_id: number;
  }>> {
    return this.request('/lottery/sections/2d');
  }

  async getLotteryData2D(section: string): Promise<ApiResponse<{
    section: string;
    date: string;
    game_status: string;
    is_weekend: boolean;
    is_holiday: boolean;
    is_section_open: boolean;
    overall_amount: number;
    lottery_data: Array<{
      id: number;
      bet_number: string;
      default_amount: number;
      hot_number: boolean;
      block: string;
      total_bet: number;
      total_amount: number;
      hot_amount: number;
    }>;
    category: string;
    category_id: number;
  }>> {
    return this.request('/two/lottery/data', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ section }),
    });
  }

  // 3D Lottery API methods
  async getLotterySections3D(): Promise<ApiResponse<{
    sections: Array<{
      id: number;
      time_section: string;
      open_time: string;
      close_time: string;
      is_open: boolean;
      odd: number;
      is_maintain: boolean;
      formatted_time: string;
      formatted_open_time: string;
      formatted_close_time: string;
    }>;
    total_sections: number;
    is_opened_today: boolean;
    current_time: string;
    category: string;
    category_id: number;
    opening_date: string;
  }>> {
    return this.request('/lottery/sections/3d');
  }

  async getLotteryData3D(perPage: number = 100, page: number = 1, openingDate?: string): Promise<ApiResponse<{
    game_status: string;
    lottery_data: Array<{
      id: number;
      bet_number: string;
      total_amount: number;
      hot_amount: number;
      block: string;
    }>;
    pagination: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
    };
    overall_amount: number;
    default_amount: number;
    odd: number;
    tot: number;
    month: string;
    date: number;
    now_month: string;
    category: string;
    category_id: number;
  }>> {
    let url = `/threed/lottery/data?per_page=${perPage}&page=${page}`;
    if (openingDate) {
      url += `&opening_date=${openingDate}`;
    }
    return this.request(url);
  }

  // 2D Lottery Betting API
  async placeLotteryBet2D(data: {
    section: string;
    category_id: number;
    bet_obj: Array<{
      bet_id: number;
      bet_number: string;
      amount: number;
      category_id: number;
      section: string;
    }>;
  }): Promise<ApiResponse<{
    total_amount: number;
    remaining_balance: number;
    betting_records: Array<{
      id: number;
      user_id: number;
      bet_number: string;
      amount: number;
      section: string;
      date: string;
      category_id: number;
      created_at: string;
      updated_at: string;
    }>;
    section: string;
    category: string;
    category_id: number;
  }>> {
    const response = await this.request<{
      total_amount: number;
      remaining_balance: number;
      betting_records: Array<{
        id: number;
        user_id: number;
        bet_number: string;
        amount: number;
        section: string;
        date: string;
        category_id: number;
        created_at: string;
        updated_at: string;
      }>;
      section: string;
      category: string;
      category_id: number;
    }>('/two/lottery/betting', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (response.success) {
      showSuccess('2D lottery bet placed successfully!');
    }

    return response;
  }

  // Crypto 2D Lottery API methods
  async getLotterySectionsC2D(): Promise<ApiResponse<{
    sections: Array<{
      id: number;
      time_section: string;
      open_time: string;
      close_time: string;
      is_open: boolean;
      odd: number;
      is_maintain: boolean;
      formatted_time: string;
      formatted_open_time: string;
      formatted_close_time: string;
    }>;
    total_sections: number;
    is_opened_today: boolean;
    current_time: string;
    category: string;
    category_id: number;
  }>> {
    return this.request('/lottery/sections/crypto-2d');
  }

  async getLotteryDataC2D(section: string): Promise<ApiResponse<{
    section: string;
    date: string;
    game_status: string;
    is_weekend: boolean;
    is_holiday: boolean;
    is_section_open: boolean;
    overall_amount: number;
    lottery_data: Array<{
      id: number;
      bet_number: string;
      default_amount: number;
      hot_number: boolean;
      block: string;
      total_bet: number;
      total_amount: number;
      hot_amount: number;
    }>;
    category: string;
    category_id: number;
  }>> {
    return this.request('/crypto_two/lottery/data', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ section }),
    });
  }

  // Crypto 2D Lottery Betting API
  async placeLotteryBetC2D(data: {
    section: string;
    date: string;
    bets: Array<{
      bet_number: string;
      amount: number;
      odd: number;
    }>;
  }): Promise<ApiResponse<{
    total_amount: number;
    remaining_balance: number;
    betting_records: Array<{
      id: number;
      user_id: number;
      bet_number: string;
      amount: number;
      section: string;
      date: string;
      category_id: number;
      created_at: string;
      updated_at: string;
    }>;
    section: string;
    category: string;
    category_id: number;
  }>> {
    const response = await this.request<{
      total_amount: number;
      remaining_balance: number;
      betting_records: Array<{
        id: number;
        user_id: number;
        bet_number: string;
        amount: number;
        section: string;
        date: string;
        category_id: number;
        created_at: string;
        updated_at: string;
      }>;
      section: string;
      category: string;
      category_id: number;
    }>('/cryptotwo/lottery/betting', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (response.success) {
      showSuccess('Crypto 2D lottery bet placed successfully!');
    }

    return response;
  }

  // Crypto 2D Lottery Winner List API
  async getWinnerListC2D(filters?: {
    page?: number;
    per_page?: number;
    date_from?: string;
    date_to?: string;
    section?: string;
  }): Promise<ApiResponse<{
    winners: Array<{
      id: number;
      user_name: string;
      user_phone: string;
      section: string;
      date: string;
      total_bet: number;
      total_amount: number;
      reward_amount: number;
      claim: number;
      created_at: string;
    }>;
    pagination: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
      has_more_pages: boolean;
    };
  }>> {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.section) params.append('section', filters.section);
    
    const queryString = params.toString();
    const endpoint = queryString ? `/lottery/winners/crypto-2d?${queryString}` : '/lottery/winners/crypto-2d';
    
    return this.request(endpoint);
  }

  // Crypto 2D Lottery Lucky Number History API
  async getLuckyHistoryC2D(filters?: {
    page?: number;
    per_page?: number;
    date_from?: string;
    date_to?: string;
    section?: string;
  }): Promise<ApiResponse<{
    lucky_numbers: Array<{
      id: number;
      set: string;
      value: string;
      modern: number;
      create_date: string;
      section: string;
    }>;
    pagination: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
      has_more_pages: boolean;
    };
  }>> {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.section) params.append('section', filters.section);
    
    const queryString = params.toString();
    const endpoint = queryString ? `/lottery/lucky-history/crypto-2d?${queryString}` : '/lottery/lucky-history/crypto-2d';
    
    return this.request(endpoint);
  }

  // Crypto 2D Lottery Bet History API
  async getBetHistoryC2D(filters?: {
    page?: number;
    per_page?: number;
    date_from?: string;
    date_to?: string;
    section?: string;
    status?: string;
  }): Promise<ApiResponse<{
    bets: Array<{
      id: number;
      section: string;
      date: string;
      total_bet: number;
      total_amount: number;
      reward_amount: number;
      win: number;
      claim: number;
      created_at: string;
      bet_details: Array<{
        bet_number: string;
        amount: number;
        odd: number;
        win: number;
      }>;
    }>;
    pagination: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
      has_more_pages: boolean;
    };
  }>> {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.section) params.append('section', filters.section);
    if (filters?.status) params.append('status', filters.status);
    
    const queryString = params.toString();
    const endpoint = queryString ? `/lottery/bet-history/crypto-2d?${queryString}` : '/lottery/bet-history/crypto-2d';
    
    return this.request(endpoint);
  }

  // 3D Lottery Betting API
  async placeLotteryBet3D(data: {
    section: string;
    category_id: number;
    bet_obj: Array<{
      bet_number: string;
      amount: number;
      odd: number;
      date_3d: string;
      category_id: number;
      section: string;
    }>;
  }): Promise<ApiResponse<{
    total_amount: number;
    remaining_balance: number;
    betting_records: Array<{
      id: number;
      user_id: number;
      bet_number: string;
      amount: number;
      section: string;
      date: string;
      category_id: number;
      created_at: string;
      updated_at: string;
    }>;
    section: string;
    category: string;
    category_id: number;
  }>> {
    const response = await this.request<{
      total_amount: number;
      remaining_balance: number;
      betting_records: Array<{
        id: number;
        user_id: number;
        bet_number: string;
        amount: number;
        section: string;
        date: string;
        category_id: number;
        created_at: string;
        updated_at: string;
      }>;
      section: string;
      category: string;
      category_id: number;
    }>('/threelottery/betting', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (response.success) {
      showSuccess('3D lottery bet placed successfully!');
    }

    return response;
  }

  // 3D Lottery Winner List API
  async getWinnerList3D(filters?: {
    page?: number;
    per_page?: number;
    date_from?: string;
    date_to?: string;
    section?: string;
    min_amount?: number;
  }): Promise<ApiResponse<{
    winners: Array<{
      id: number;
      user_name: string;
      user_phone: string;
      section: string;
      date: string;
      total_bet: number;
      total_amount: number;
      reward_amount: number;
      claim: number;
      created_at: string;
    }>;
    pagination: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
      has_more_pages: boolean;
    };
  }>> {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.section) params.append('section', filters.section);
    if (filters?.min_amount) params.append('min_amount', filters.min_amount.toString());
    
    const queryString = params.toString();
    const endpoint = queryString ? `/lottery/winners/3d?${queryString}` : '/lottery/winners/3d';
    
    return this.request(endpoint);
  }

  // 3D Lottery Lucky Number History API
  async getLuckyHistory3D(filters?: {
    page?: number;
    per_page?: number;
    date_from?: string;
    date_to?: string;
    section?: string;
  }): Promise<ApiResponse<{
    lucky_numbers: Array<{
      id: number;
      set: string;
      value: string;
      modern: number;
      create_date: string;
      section: string;
    }>;
    pagination: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
      has_more_pages: boolean;
    };
  }>> {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.section) params.append('section', filters.section);
    
    const queryString = params.toString();
    const endpoint = queryString ? `/lottery/lucky-history/3d?${queryString}` : '/lottery/lucky-numbers/3d';
    
    return this.request(endpoint);
  }

  // 3D Lottery Bet History API
  async getBetHistory3D(filters?: {
    page?: number;
    per_page?: number;
    date_from?: string;
    date_to?: string;
    section?: string;
    status?: string;
  }): Promise<ApiResponse<{
    bets: Array<{
      id: number;
      section: string;
      date: string;
      total_bet: number;
      total_amount: number;
      reward_amount: number;
      win: number; // 0 = pending, 1 = win, 2 = lose
      claim: number;
      created_at: string;
      bet_details: Array<{
        bet_number: string;
        amount: number;
        odd: number;
        win: number; // 0 = pending, 1 = win, 2 = lose
      }>;
    }>;
    pagination: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
      has_more_pages: boolean;
    };
  }>> {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.section) params.append('section', filters.section);
    if (filters?.status) params.append('status', filters.status);
    
    const queryString = params.toString();
    const endpoint = queryString ? `/user/bet-history/3d?${queryString}` : '/user/bet-history/3d';
    
    return this.request(endpoint);
  }

  // 2D Lottery Winner List API
  async getWinnerList2D(filters?: {
    page?: number;
    per_page?: number;
    date_from?: string;
    date_to?: string;
    section?: string;
    min_amount?: number;
  }): Promise<ApiResponse<{
    winners: Array<{
      id: number;
      user_name: string;
      user_phone: string;
      section: string;
      date: string;
      total_bet: number;
      total_amount: number;
      reward_amount: number;
      claim: number;
      created_at: string;
    }>;
    pagination: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
      has_more_pages: boolean;
    };
  }>> {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.section) params.append('section', filters.section);
    if (filters?.min_amount) params.append('min_amount', filters.min_amount.toString());
    
    const queryString = params.toString();
    const endpoint = queryString ? `/lottery/winners/2d?${queryString}` : '/lottery/winners/2d';
    
    return this.request(endpoint);
  }

  // 2D Lottery Lucky Number History API
  async getLuckyHistory2D(filters?: {
    page?: number;
    per_page?: number;
    date_from?: string;
    date_to?: string;
    section?: string;
  }): Promise<ApiResponse<{
    lucky_numbers: Array<{
      id: number;
      lucky_number: string;
      section: string;
      lottery_type: string;
      category_id: number;
      category_name: string;
      create_date: string;
      set: number;
      value: number;
      modern: number;
    }>;
    pagination: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
      has_more_pages: boolean;
    };
  }>> {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.section) params.append('section', filters.section);
    
    const queryString = params.toString();
    const endpoint = queryString ? `/lottery/lucky-history/2d?${queryString}` : '/lottery/lucky-history/2d';
    
    return this.request(endpoint);
  }

  // 2D Lottery Bet History API
  async getBetHistory2D(filters?: {
    page?: number;
    per_page?: number;
    date_from?: string;
    date_to?: string;
    section?: string;
    status?: string;
  }): Promise<ApiResponse<{
    bets: Array<{
      id: number;
      section: string;
      date: string;
      total_bet: number;
      total_amount: number;
      reward_amount: number;
      win: number; // 0 = pending, 1 = win, 2 = lose
      claim: number;
      created_at: string;
      bet_details: Array<{
        bet_number: string;
        amount: number;
        odd: number;
        win: number; // 0 = pending, 1 = win, 2 = lose
      }>;
    }>;
    pagination: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
      has_more_pages: boolean;
    };
  }>> {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.section) params.append('section', filters.section);
    if (filters?.status) params.append('status', filters.status);
    
    const queryString = params.toString();
    const endpoint = queryString ? `/user/bet-history/2d?${queryString}` : '/user/bet-history/2d';
    
    return this.request(endpoint);
  }
}

export const apiService = new ApiService();
export default apiService;
